import { Box, Tooltip, Typography } from "@mui/joy";
import moment from "moment-timezone";
import { Gig } from "../../entities/gig";
import { Project } from "../../entities/project";
import { Stage } from "../../entities/stage";
import { Venue } from "../../entities/venue";
import { ResolvedGig } from "./servicesList";

const appleRed = "#FF3B30";
const navy = "#081D3A";

const frosted = {
  background: "rgba(0, 0, 0, 0.42)",
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
} as const;

/**
 * Relative countdown wording for an upcoming service.
 *
 * @param gig the service
 * @param tz venue timezone
 * @returns {string | undefined} the label
 */
function relativeLabel(gig: Gig, tz?: string): string | undefined {
  if (gig.toBeDetermined() || gig.completed(tz)) return undefined;
  // Bucket both the service date and "now" in the same (venue) zone so the
  // day delta can't drift by one near midnight for cross-timezone viewers.
  const days = gig
    .from(tz)
    .clone()
    .startOf("day")
    .diff(moment.tz(gig.resolvedTz(tz)).startOf("day"), "days");
  if (days <= 0) return "Today";
  if (days === 1) return "Tomorrow";
  return `In ${days} days`;
}

/**
 * Span covered by the group's dated services, e.g. "JUL 1 2026",
 * "JUL 1–2 2026" or "JUN 25 – JUL 2 2026". Mirrors the mobile ServiceGroupTile
 * wording (no weekday, year from the last service).
 *
 * @param items the group's resolved services, sorted ascending
 * @returns {string} the formatted range (empty when every service is TBD)
 */
function dateRange(items: ResolvedGig[]): string {
  const dated = items.filter((i) => !i.gig.toBeDetermined());
  if (!dated.length) return "";
  const a = dated[0];
  const b = dated[dated.length - 1];
  const start = a.gig.from(a.venue?.tzName);
  const end = b.gig.from(b.venue?.tzName);
  const startMonth = start.format("MMM").toUpperCase();
  const endMonth = end.format("MMM").toUpperCase();
  const startDay = start.format("D");
  const endDay = end.format("D");
  const year = end.format("YYYY");
  if (startMonth === endMonth && startDay === endDay)
    return `${startMonth} ${startDay} ${year}`;
  if (startMonth === endMonth)
    return `${startMonth} ${startDay}–${endDay} ${year}`;
  return `${startMonth} ${startDay} – ${endMonth} ${endDay} ${year}`;
}

/**
 * A single session inside a project card: a centered date block (weekday, day,
 * countdown) on the left, then the session type + Playing chip, time, venue and
 * stage. Flat — no card chrome of its own, since the project card supplies it.
 * Mirrors the mobile ServiceGroupTile session row.
 *
 * @param root0 props
 * @param root0.gig the service
 * @param root0.venue resolved venue
 * @param root0.stage resolved stage
 * @param root0.divider whether to draw a hairline above the row
 * @param root0.onClick tap handler
 * @returns {ReactElement} ServiceRow
 */
function ServiceRow({
  gig,
  venue,
  stage,
  divider,
  onClick,
}: {
  gig: Gig;
  venue?: Venue;
  stage?: Stage;
  divider: boolean;
  onClick: (gig: Gig) => void;
}) {
  const tz = venue?.tzName;
  const tbd = gig.toBeDetermined();
  const today = gig.happeningToday(tz);
  const countdown = relativeLabel(gig, tz);
  const playing = gig.isPlaying();
  const stageColor = stage?.color || "#8E8E93";
  const showStage = stage && gig.stageID > 0 && playing;
  // The group header already names the project, so the row leads with the
  // session type rather than the (often identical) gig title.
  const sessionType = gig.sessionType() || "Service";
  const timeLabel = tbd
    ? "Time to be determined"
    : gig.noTime(tz)
    ? ""
    : `${gig.startTime(tz)} – ${gig.endTime(tz)} ${gig.from(tz).format("z")}`;
  const venueLabel =
    gig.venueID === -1
      ? "Venue to be determined"
      : venue
      ? `${venue.name} · ${venue.city}, ${venue.state}`
      : "Unknown venue";

  return (
    <Box
      sx={{
        borderTop: divider ? "1px solid var(--mui-palette-divider)" : "none",
      }}
    >
      <Box
        onClick={(e) => {
          e.stopPropagation();
          onClick(gig);
        }}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
          py: 1.375,
          cursor: "pointer",
          transition: "background 0.15s ease",
          "&:hover": { background: "rgba(120, 120, 128, 0.08)" },
          "&:active": { background: "rgba(120, 120, 128, 0.14)" },
        }}
      >
        {/* Date block — weekday, day, countdown, centered */}
        <Box
          sx={{
            width: 60,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            lineHeight: 1,
          }}
        >
          {tbd ? (
            <Typography
              sx={{ fontSize: 16, color: "var(--mui-palette-text-tertiary)" }}
            >
              <i className="fa-solid fa-question"></i>
            </Typography>
          ) : (
            <>
              <Typography
                sx={{
                  fontSize: "0.625rem",
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  lineHeight: 1.1,
                  color: appleRed,
                }}
              >
                {gig.from(tz).format("ddd").toUpperCase()}
              </Typography>
              <Typography
                sx={{
                  fontSize: "1.25rem",
                  fontWeight: 700,
                  lineHeight: 1.05,
                  color: today ? appleRed : "var(--mui-palette-text-primary)",
                }}
              >
                {gig.from(tz).format("D")}
              </Typography>
              {countdown ? (
                <Typography
                  sx={{
                    fontSize: "0.6875rem",
                    fontWeight: 600,
                    textAlign: "center",
                    lineHeight: 1.15,
                    mt: 0.375,
                    color: today
                      ? appleRed
                      : "var(--mui-palette-text-tertiary)",
                  }}
                >
                  {countdown}
                </Typography>
              ) : (
                []
              )}
            </>
          )}
        </Box>

        {/* Session type, time, venue, stage */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "0.9375rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
              }}
            >
              {sessionType}
            </Typography>
            <Typography
              sx={{
                flexShrink: 0,
                fontSize: "0.6875rem",
                fontWeight: 600,
                whiteSpace: "nowrap",
                ...(playing
                  ? {
                      color: "#1B7A33",
                      '[data-joy-color-scheme="dark"] &, [data-mui-color-scheme="dark"] &':
                        { color: "#5DE27E" },
                    }
                  : {
                      color: "#fff",
                      background: "#C9251C",
                      px: 0.75,
                      py: 0.125,
                      borderRadius: "999px",
                    }),
              }}
            >
              {playing ? "Playing" : "Not playing"}
            </Typography>
          </Box>
          {timeLabel ? (
            <Typography
              level="body3"
              sx={{ color: "var(--mui-palette-text-secondary)", mt: 0.375 }}
              startDecorator={
                <i className="fa-regular fa-clock" style={{ fontSize: 11 }}></i>
              }
            >
              {timeLabel}
            </Typography>
          ) : (
            []
          )}
          <Typography
            level="body3"
            sx={{
              color: "var(--mui-palette-text-secondary)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              mt: 0.375,
            }}
            startDecorator={
              <i
                className="fa-solid fa-location-dot"
                style={{ fontSize: 11 }}
              ></i>
            }
          >
            {venueLabel}
          </Typography>
          {showStage ? (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                px: 1,
                py: 0.25,
                mt: 0.75,
                borderRadius: "999px",
                background: `${stageColor}1A`,
              }}
            >
              {gig.isCattleCall() ? (
                <i
                  className="fa-solid fa-cowbell fa-shake"
                  style={{ fontSize: 12, color: stageColor }}
                />
              ) : (
                <img src={`${stage?.icon}`} style={{ height: 12 }} />
              )}
              <Typography
                sx={{ color: stageColor, fontWeight: 600, fontSize: "0.75rem" }}
              >
                {stage?.wording()}
              </Typography>
            </Box>
          ) : (
            []
          )}
        </Box>
      </Box>
    </Box>
  );
}

/**
 * Cover-led card grouping a run of consecutive services that share the same
 * project — the web counterpart of the mobile Journal's ServiceGroupTile. One
 * shared hero banner (project artwork, or a branded gradient) carries the date
 * span and organization; the footer lists the project name, service count and
 * each session as a tappable row. Lone services keep the standalone ServiceTile.
 *
 * @param root0 props
 * @param root0.project the resolved project (may be undefined — name falls back
 *   to the lead service's title), its coverURL is the hero
 * @param root0.items the project's resolved services, sorted by date ascending
 * @param root0.index position (for the staggered entrance animation)
 * @param root0.onClick service tap handler
 * @returns {ReactElement} ProjectTile
 */
export function ProjectTile({
  project,
  items,
  index = 0,
  onClick,
}: {
  project?: Project;
  items: ResolvedGig[];
  index?: number;
  onClick: (gig: Gig) => void;
}) {
  const lead = items[0];
  // Representative organization for the header (a project's services almost
  // always share one).
  const contractor = items.find((i) => i.contractor)?.contractor;
  const cover = project?.coverURL;
  const heroColor = contractor?.color || "#0A84FF";
  const locked = items.some((i) => i.gig.sourceID !== 420);
  const projectName = project?.name || lead.gig.title || "Project";
  const range = dateRange(items);

  return (
    <Box
      sx={{
        width: "100%",
        borderRadius: "20px",
        overflow: "hidden",
        background: "var(--mui-palette-background-paper, #fff)",
        border: "1px solid var(--mui-palette-divider)",
        boxShadow:
          "0 1px 2px rgba(0, 0, 0, 0.04), 0 8px 24px rgba(0, 0, 0, 0.06)",
        transition: "box-shadow 0.25s cubic-bezier(0.3, 0, 0.2, 1)",
        animation: "cardIn 0.3s ease both",
        animationDelay: `${Math.min(index, 12) * 0.04}s`,
        display: "flex",
        flexDirection: "column",
        "&:hover": {
          boxShadow:
            "0 4px 8px rgba(0, 0, 0, 0.06), 0 16px 40px rgba(0, 0, 0, 0.12)",
        },
      }}
    >
      {/* Hero — square when there's project artwork (shown in full), a short
          banner otherwise */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          aspectRatio: cover ? "1 / 1" : "16 / 7",
          overflow: "hidden",
          flexShrink: 0,
          background: cover
            ? "#000"
            : `linear-gradient(135deg, ${heroColor} 0%, ${navy} 130%)`,
        }}
      >
        {cover ? (
          <Box
            component="img"
            src={cover}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        ) : (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "rgba(255, 255, 255, 0.22)",
              fontSize: 48,
            }}
          >
            <i className="fa-regular fa-folder-open"></i>
          </Box>
        )}
        {/* Scrim so the chips stay legible on any artwork */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.30) 0%, rgba(0,0,0,0) 30%, rgba(0,0,0,0) 70%, rgba(0,0,0,0.25) 100%)",
          }}
        />

        {/* Date range — top-left */}
        {range ? (
          <Box
            sx={{
              position: "absolute",
              top: 12,
              left: 12,
              px: 1.25,
              py: 0.625,
              borderRadius: "999px",
              maxWidth: "60%",
              ...frosted,
            }}
          >
            <Typography
              sx={{
                color: "#fff",
                fontWeight: 700,
                fontSize: "0.6875rem",
                letterSpacing: "0.04em",
              }}
              noWrap
            >
              {range}
            </Typography>
          </Box>
        ) : (
          []
        )}

        {/* Organization — top-right */}
        {contractor?.coverURL ? (
          <Tooltip size="sm" arrow title={contractor?.name}>
            <Box
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                background: contractor.color || "#fff",
                borderRadius: "8px",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.25)",
              }}
            >
              <img
                src={contractor.coverURL}
                height={26}
                style={{
                  maxWidth: 120,
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </Box>
          </Tooltip>
        ) : contractor?.name ? (
          <Box
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              px: 1,
              py: 0.5,
              borderRadius: "999px",
              maxWidth: "55%",
              ...frosted,
            }}
          >
            <Typography
              sx={{ color: "#fff", fontWeight: 600, fontSize: "0.75rem" }}
              noWrap
            >
              {contractor.name}
            </Typography>
          </Box>
        ) : (
          []
        )}
      </Box>

      {/* Footer — project title + the session list */}
      <Box sx={{ px: 1.75, pt: 1.5, pb: 0.75 }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "1.0625rem",
            letterSpacing: "-0.01em",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          startDecorator={
            locked ? (
              <i
                className="fa-solid fa-lock"
                style={{
                  fontSize: 11,
                  color: "var(--mui-palette-text-tertiary)",
                }}
              ></i>
            ) : undefined
          }
        >
          {projectName}
        </Typography>
        <Typography
          level="body3"
          sx={{
            color: "var(--mui-palette-text-secondary)",
            fontWeight: 500,
            mt: 0.25,
          }}
        >
          {items.length} services
        </Typography>
        <Box sx={{ mt: 0.5 }}>
          {items.map((item, i) => (
            <ServiceRow
              key={item.gig.id}
              gig={item.gig}
              venue={item.venue}
              stage={item.stage}
              divider={i > 0}
              onClick={onClick}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
}
